import React from 'react';
import { FileText, FileSpreadsheet, Table, Check, Loader, AlertCircle, Eye, Download, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

interface Upload {
  upload_id: string;
  filename: string;
  file_size: number;
  file_type: string;
  template_type: string;
  upload_status: string;
  total_rows: number | null;
  processed_rows: number | null;
  error_message: string | null;
  created_at: string | null;
  updated_at: string | null;
}

interface DocumentCardProps {
  document: Upload;
  onView: (document: Upload) => void;
  onDownload?: (document: Upload) => void;
  onDelete?: (document: Upload) => void;
}

const DocumentCard: React.FC<DocumentCardProps> = ({ document, onView, onDownload, onDelete }) => {
  // Handle delete click (show not implemented message)
  const handleDeleteClick = () => {
    if (onDelete) {
      onDelete(document);
    } else {
      toast.info('Delete feature coming soon');
    }
  };

  // Handle download click (show not implemented message)
  const handleDownloadClick = () => {
    if (onDownload) {
      onDownload(document);
    } else {
      toast.info('Download feature coming soon');
    }
  };

  // Determine file type icon
  const getFileTypeIcon = () => {
    if (document.file_type.includes('pdf')) {
      return <FileText className="w-5 h-5 text-red-600" />;
    } else if (document.file_type.includes('excel') || document.file_type.includes('spreadsheet')) {
      return <FileSpreadsheet className="w-5 h-5 text-green-600" />;
    } else if (document.file_type.includes('csv')) {
      return <Table className="w-5 h-5 text-blue-600" />;
    }
    return <FileText className="w-5 h-5 text-gray-600" />;
  };

  // Get extraction status badge
  const getStatusBadge = () => {
    const status = document.upload_status.toLowerCase();

    if (status === 'complete' || status === 'ready' || status === 'approved' || status === 'extracted') {
      return (
        <div className="flex items-center gap-1 px-2 py-1 bg-green-50 border border-green-200 rounded text-xs text-green-700">
          <Check className="w-3 h-3" />
          <span>Ready</span>
        </div>
      );
    } else if (status === 'processing' || status === 'ingesting' || status === 'uploading') {
      return (
        <div className="flex items-center gap-1 px-2 py-1 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
          <Loader className="w-3 h-3 animate-spin" />
          <span>Processing</span>
        </div>
      );
    } else if (status === 'error' || status === 'failed') {
      return (
        <div className="flex items-center gap-1 px-2 py-1 bg-red-50 border border-red-200 rounded text-xs text-red-700">
          <AlertCircle className="w-3 h-3" />
          <span>Error</span>
        </div>
      );
    } else if (status === 'initialized') {
      return (
        <div className="flex items-center gap-1 px-2 py-1 bg-gray-50 border border-gray-200 rounded text-xs text-gray-700">
          <span>Initialized</span>
        </div>
      );
    }

    return (
      <div className="px-2 py-1 bg-gray-50 border border-gray-200 rounded text-xs text-gray-600">
        {status}
      </div>
    );
  };

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
  };

  // Format date
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Get file type label
  const getFileTypeLabel = (): string => {
    if (document.file_type.includes('pdf')) return 'PDF';
    if (document.file_type.includes('excel') || document.file_type.includes('spreadsheet')) return 'Excel';
    if (document.file_type.includes('csv')) return 'CSV';
    return 'Document';
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors">
      <div className="flex items-start justify-between">
        {/* Left side: Icon and metadata */}
        <div className="flex items-start gap-3 flex-1">
          {/* File type icon */}
          <div className="mt-1">{getFileTypeIcon()}</div>

          {/* Metadata */}
          <div className="flex-1 min-w-0">
            <h4 className="font-medium text-gray-900 truncate">{document.filename}</h4>

            <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
              <span className="font-medium text-gray-600">{getFileTypeLabel()}</span>
              <span>•</span>
              <span>{formatFileSize(document.file_size)}</span>
              <span>•</span>
              <span>{formatDate(document.created_at)}</span>
            </div>

            {/* Additional metadata */}
            <div className="flex items-center gap-3 mt-1 text-xs text-gray-600">
              {document.template_type && (
                <>
                  <span className="capitalize">{document.template_type.replace('_', ' ')}</span>
                  <span>•</span>
                </>
              )}
              {document.total_rows !== null && (
                <span>{document.total_rows.toLocaleString()} rows</span>
              )}
            </div>

            {/* Error message if exists */}
            {document.error_message && (
              <div className="mt-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1">
                {document.error_message}
              </div>
            )}
          </div>
        </div>

        {/* Right side: Status badge and actions */}
        <div className="flex items-center gap-3 ml-4">
          {/* Status badge */}
          {getStatusBadge()}

          {/* Action buttons */}
          <div className="flex items-center gap-1">
            {/* View button */}
            <button
              onClick={() => onView(document)}
              title="Preview document"
              className="p-2 hover:bg-gray-100 rounded-md transition-colors"
            >
              <Eye className="w-4 h-4 text-gray-600 hover:text-gray-900" />
            </button>

            {/* Download button */}
            <button
              onClick={handleDownloadClick}
              title="Download document"
              className="p-2 hover:bg-gray-100 rounded-md transition-colors"
            >
              <Download className="w-4 h-4 text-gray-600 hover:text-gray-900" />
            </button>

            {/* Delete button */}
            <button
              onClick={handleDeleteClick}
              title="Delete document"
              className="p-2 hover:bg-red-50 rounded-md transition-colors"
            >
              <Trash2 className="w-4 h-4 text-red-600 hover:text-red-700" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentCard;
